import { createI18n } from 'vue-i18n'
import zhCN from './zh-CN'
import enUS from './en-US'

export type AppLocale = 'zh-CN' | 'en-US'

export const LOCALE_STORAGE_KEY = 'miaobi-locale'
export const DEFAULT_LOCALE: AppLocale = 'zh-CN'
export const SUPPORTED_LOCALES: AppLocale[] = ['zh-CN', 'en-US']

const matchSupportedLocale = (locale?: string | null): AppLocale | undefined => {
  const values = (locale || '')
    .split(',')
    .map(item => item.split(';')[0].trim().toLowerCase())
    .filter(Boolean)

  for (const value of values) {
    if (value.startsWith('zh')) {
      return 'zh-CN'
    }
    if (value.startsWith('en')) {
      return 'en-US'
    }
  }
}

export const normalizeLocale = (locale?: string | null): AppLocale => {
  return matchSupportedLocale(locale) || DEFAULT_LOCALE
}

const getStoredLocale = (): AppLocale | undefined => {
  try {
    return matchSupportedLocale(localStorage.getItem(LOCALE_STORAGE_KEY))
  } catch (error) {
    return undefined
  }
}

const getBrowserLocale = (): AppLocale | undefined => {
  if (typeof navigator === 'undefined') {
    return undefined
  }
  const browserLocales = navigator.languages?.length ? navigator.languages.join(',') : navigator.language
  return matchSupportedLocale(browserLocales)
}

export const getSavedLocale = (): AppLocale => {
  return getStoredLocale() || getBrowserLocale() || matchSupportedLocale(import.meta.env.VITE_APP_LOCALE) || DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: getSavedLocale(),
  fallbackLocale: 'zh-CN',
  messages: {
    'zh-CN': zhCN,
    'en-US': enUS,
  },
})

export const getCurrentLocale = (): AppLocale => {
  return normalizeLocale((i18n.global.locale as any).value)
}

export const getTUIKitLocale = (locale: AppLocale = getCurrentLocale()): string => {
  return locale === 'zh-CN' ? 'zh_cn' : 'en'
}

export const syncTUIKitLocale = (locale: AppLocale = getCurrentLocale()): void => {
  import('@tencentcloud/chat-uikit-engine')
    .then(({ TUITranslateService }) => {
      TUITranslateService.changeLanguage(getTUIKitLocale(locale))
    })
    .catch(() => {})
}

const applyLocale = (locale: string): AppLocale => {
  const normalized = normalizeLocale(locale)
  ;(i18n.global.locale as any).value = normalized
  document.documentElement.lang = normalized
  syncTUIKitLocale(normalized)
  return normalized
}

export const setLocale = (locale: string): AppLocale => {
  const normalized = applyLocale(locale)
  localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
  return normalized
}

applyLocale(getSavedLocale())

export default i18n
